import { useEffect, useState, type CSSProperties, type FormEvent } from 'react';

type Account = {
  user: {
    displayName: string;
    email: string;
  };
  workspace: {
    name: string;
  };
};

const fieldStyle: CSSProperties = {
  background: 'transparent',
  border: '1px solid var(--border)',
  borderRadius: 8,
  color: 'inherit',
  fontSize: 15,
  padding: '10px 12px',
  width: '100%',
};

const buttonStyle: CSSProperties = {
  background: 'hsl(var(--primary))',
  border: 0,
  borderRadius: 8,
  color: 'hsl(var(--primary-foreground))',
  cursor: 'pointer',
  fontSize: 14,
  fontWeight: 700,
  padding: '10px 16px',
};

async function responseError(response: Response): Promise<string> {
  const body = await response.json().catch(() => null) as {
    error?: { message?: string };
    message?: string;
  } | null;
  return body?.error?.message ?? body?.message ?? `请求失败（HTTP ${response.status}）`;
}

async function currentAccount(): Promise<Account | null> {
  const sessionResponse = await fetch('/proxy/capworks/api/auth/get-session', {
    credentials: 'include',
    headers: { accept: 'application/json' },
  });
  if (!sessionResponse.ok) throw new Error(await responseError(sessionResponse));
  if (!await sessionResponse.json()) return null;

  const accountResponse = await fetch('/proxy/capworks/v1/account/current', {
    credentials: 'include',
    headers: { accept: 'application/json' },
  });
  if (!accountResponse.ok) throw new Error(await responseError(accountResponse));
  return accountResponse.json() as Promise<Account>;
}

export function AuthPage() {
  const [account, setAccount] = useState<Account | null>(null);
  const [busy, setBusy] = useState(true);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('正在检查登录状态…');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);

  useEffect(() => {
    void currentAccount()
      .then((value) => {
        setAccount(value);
        setMessage(value ? '已登录，可以直接在 API Reference 中发送请求。' : '尚未登录。');
      })
      .catch((cause) => {
        setError(cause instanceof Error ? cause.message : String(cause));
        setMessage('登录状态检查失败。');
      })
      .finally(() => setBusy(false));
  }, []);

  async function sendCode(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const response = await fetch('/proxy/capworks/api/auth/email-otp/send-verification-otp', {
        body: JSON.stringify({ email: email.trim(), type: 'sign-in' }),
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        method: 'POST',
      });
      if (!response.ok) throw new Error(await responseError(response));
      setOtpSent(true);
      setMessage('验证码已发送，有效期 10 分钟。');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  }

  async function signIn(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const signInResponse = await fetch('/proxy/capworks/api/auth/sign-in/email-otp', {
        body: JSON.stringify({ email: email.trim(), otp: otp.trim(), name: email.split('@')[0] }),
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        method: 'POST',
      });
      if (!signInResponse.ok) throw new Error(await responseError(signInResponse));

      const bootstrapResponse = await fetch('/proxy/capworks/v1/account/bootstrap', {
        body: '{}',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        method: 'POST',
      });
      if (!bootstrapResponse.ok) throw new Error(await responseError(bootstrapResponse));
      const result = await bootstrapResponse.json() as { account: Account };
      setAccount(result.account);
      setMessage('登录成功，可以直接在 API Reference 中发送请求。');
      setOtp('');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  }

  async function signOut() {
    setBusy(true);
    setError('');
    try {
      const response = await fetch('/proxy/capworks/api/auth/sign-out', {
        body: '{}',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        method: 'POST',
      });
      if (!response.ok) throw new Error(await responseError(response));
      setAccount(null);
      setOtpSent(false);
      setMessage('已退出登录。');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main style={{ margin: '0 auto', maxWidth: 760, padding: '32px 20px 64px' }}>
      <p style={{ color: 'var(--muted-foreground)', fontSize: 14, fontWeight: 650, letterSpacing: '0.08em', margin: 0, textTransform: 'uppercase' }}>
        Better Auth
      </p>
      <h1 style={{ fontSize: 36, letterSpacing: '-0.03em', lineHeight: 1.15, margin: '8px 0 10px' }}>
        登录并测试 API
      </h1>
      <p style={{ color: 'var(--muted-foreground)', fontSize: 16, lineHeight: 1.7, margin: '0 0 24px' }}>
        使用 Email OTP 建立文档站会话。登录成功后，浏览器会自动携带 HttpOnly Cookie；无需复制 Token，也不要把验证码或 Cookie 分享给他人。
      </p>

      <section style={{ border: '1px solid var(--border)', borderRadius: 12, padding: 20 }}>
        <div style={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'space-between' }}>
          <div>
            <strong>{account ? account.user.displayName : '会话状态'}</strong>
            <div style={{ color: 'var(--muted-foreground)', fontSize: 14, marginTop: 4 }}>
              {account ? `${account.user.email} · ${account.workspace.name}` : message}
            </div>
          </div>
          {account && (
            <button disabled={busy} onClick={() => void signOut()} style={{ ...buttonStyle, opacity: busy ? 0.6 : 1 }} type="button">
              退出登录
            </button>
          )}
        </div>

        {!account && (
          <div style={{ display: 'grid', gap: 18, marginTop: 22 }}>
            <form onSubmit={sendCode} style={{ display: 'grid', gap: 10 }}>
              <label htmlFor="docs-auth-email" style={{ fontSize: 14, fontWeight: 650 }}>邮箱</label>
              <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'minmax(0, 1fr) auto' }}>
                <input
                  autoComplete="email"
                  disabled={busy}
                  id="docs-auth-email"
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="you@example.com"
                  required
                  style={fieldStyle}
                  type="email"
                  value={email}
                />
                <button disabled={busy} style={{ ...buttonStyle, opacity: busy ? 0.6 : 1 }} type="submit">
                  发送验证码
                </button>
              </div>
            </form>

            {otpSent && (
              <form onSubmit={signIn} style={{ display: 'grid', gap: 10 }}>
                <label htmlFor="docs-auth-otp" style={{ fontSize: 14, fontWeight: 650 }}>6 位验证码</label>
                <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'minmax(0, 1fr) auto' }}>
                  <input
                    autoComplete="one-time-code"
                    disabled={busy}
                    id="docs-auth-otp"
                    inputMode="numeric"
                    maxLength={6}
                    onChange={(event) => setOtp(event.target.value)}
                    pattern="[0-9]{6}"
                    placeholder="000000"
                    required
                    style={fieldStyle}
                    value={otp}
                  />
                  <button disabled={busy} style={{ ...buttonStyle, opacity: busy ? 0.6 : 1 }} type="submit">
                    登录
                  </button>
                </div>
              </form>
            )}
          </div>
        )}

        {error && (
          <p role="alert" style={{ background: 'color-mix(in srgb, #ef4444 12%, transparent)', borderRadius: 8, color: '#dc2626', margin: '18px 0 0', padding: '10px 12px' }}>
            {error}
          </p>
        )}
      </section>

      <section style={{ marginTop: 28 }}>
        <h2 style={{ fontSize: 22, marginBottom: 10 }}>接下来</h2>
        <ol style={{ color: 'var(--muted-foreground)', lineHeight: 1.8, paddingLeft: 22 }}>
          <li>打开 <a href="/api/capworks">CapWorks HTTP API</a>。</li>
          <li>选择接口，例如 <code>GET /v1/account/current</code>。</li>
          <li>点击 <strong>Test</strong>，填写参数后点击 <strong>Send</strong>。</li>
        </ol>
      </section>
    </main>
  );
}
